import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as ConnectedIcon,
  Cancel as DisconnectedIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { PageTitleContainer } from '../components/common';

const FigmaLogo = () => (
  <svg width="24" height="24" viewBox="0 0 38 57" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z" fill="#1ABCFE" />
    <path d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z" fill="#0ACF83" />
    <path d="M19 0V19H28.5C33.7467 19 38 14.7467 38 9.5C38 4.25329 33.7467 0 28.5 0H19Z" fill="#FF7262" />
    <path d="M0 9.5C0 14.7467 4.25329 19 9.5 19H19V0H9.5C4.25329 0 0 4.25329 0 9.5Z" fill="#F24E1E" />
    <path d="M0 28.5C0 33.7467 4.25329 38 9.5 38H19V19H9.5C4.25329 19 0 23.2533 0 28.5Z" fill="#A259FF" />
  </svg>
);

const ProjectIntegrations = () => {
  const { projectId } = useParams();
  const [figmaStatus, setFigmaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/integrations/figma/status');
      setFigmaStatus(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching Figma status:', err);
      setError('Failed to check Figma connection status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();

    // Check if we just came back from OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('figma') === 'connected') {
      fetchStatus();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const response = await api.get('/integrations/figma/connect');
      const { authorization_url } = response.data.data;
      // Open Figma auth in new tab
      window.open(authorization_url, '_blank', 'width=600,height=700');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to start Figma connection';
      setError(detail);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await api.delete('/integrations/figma/disconnect');
      await fetchStatus();
    } catch (err) {
      setError('Failed to disconnect Figma');
    } finally {
      setDisconnecting(false);
    }
  };

  const isConnected = figmaStatus?.connected;
  const method = figmaStatus?.method;
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');
  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);

  return (
    <Box sx={{
      width: '100%',
      bgcolor: '#f5f6f7',
      minHeight: '100vh',
      pb: 4,
      px: 6,
      pt: 4,
      overflowX: 'hidden',
      backgroundImage: 'none', // Explicitly remove any background image
      '&::before': { display: 'none' },
      '&::after': { display: 'none' }
    }}>
      <PageTitleContainer
        title="Integrations"
        justifyContent={'space-between'}

        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 3,
          mt: 0,
          py: '16px' // Exact 16px padding top and bottom
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Connect external services to enhance your project's design workflow.
        </Typography>
      </PageTitleContainer>

      {error && (
        <Alert severity="error" sx={{
          mb: "16px", boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '4px',
            p: { xs: 2, sm: "12px 12px 12px 12px" },
            width: '100%',
            height: contentHeight,
            bgcolor: 'white',
            overflowX: 'hidden',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            backgroundImage: 'none',
            '&::before': {
              display: 'none'
            },
            '&::after': {
              display: 'none'
            }
          }}
        >
          {/* Figma Integration */}
          <Card variant="outlined" sx={{ mb: 2, boxShadow: "none !important", borderRadius: "4px" }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FigmaLogo />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Figma
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Import design systems, components, colors, and typography from Figma files.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : isConnected ? (
                    <>
                      <Chip
                        icon={<ConnectedIcon />}
                        label={method === 'oauth' ? 'Connected (OAuth)' : 'Connected (Token)'}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                      <Tooltip title="Refresh status">
                        <IconButton size="small" onClick={fetchStatus}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={disconnecting ? <CircularProgress size={14} /> : <LinkOffIcon />}
                        onClick={handleDisconnect}
                        disabled={disconnecting || method === 'personal_access_token'}
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <>
                      <Chip
                        icon={<DisconnectedIcon />}
                        label="Not connected"
                        color="default"
                        size="small"
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: "auto !important",
                          mr: 2
                        }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={connecting ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
                        onClick={handleConnect}
                        disabled={connecting}
                        sx={{ textTransform: 'none' }}
                      >
                        Connect Figma
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              {isConnected && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    When connected, paste any Figma URL in a design task conversation and the system will
                    automatically fetch the file structure, styles, components, and typography.
                  </Typography>
                </Box>
              )}

              {!isConnected && figmaStatus?.oauth_available === false && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Figma OAuth is not configured. Ask your admin to set <code>FIGMA_CLIENT_ID</code> and{' '}
                  <code>FIGMA_CLIENT_SECRET</code> in the backend environment, or set{' '}
                  <code>FIGMA_ACCESS_TOKEN</code> for Personal Access Token authentication.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* <Divider sx={{ my: 3 }} /> */}

          {/* Placeholder for future integrations */}
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            More integrations coming soon — Google Drive, Slack, Jira, and more.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default ProjectIntegrations;
